import { formatPhone } from '@chiroup/core/functions/format';
import { ReportLocationInfo } from '@chiroup/core/types/ReportPrint.type';
import { Phone as PhoneIcon } from 'lucide-react';
import React from 'react';
import { Icon } from '../Icon';
import { Icons } from '../icons';

type Props = {
  clinicName?: string;
  pdf?: boolean;
  blobUrl?: string;
  /** Already-resolved logo URL — see resolveLetterheadLogoUrl. */
  logoUrl?: string;
  locationInfo: ReportLocationInfo;
};

const ReportPrintLetterhead: React.FC<Props> = ({
  clinicName = '',
  pdf = false,
  blobUrl = '',
  logoUrl,
  locationInfo,
}) => {
  return pdf ? (
    <div className="w-full mb-12 text-sm text-gray-800 font-semibold">
      <div className="inline-block w-full">
        {logoUrl ? (
          <div
            style={{
              width: 300,
              height: 80,
            }}
            className="float-left"
          >
            <div className="inline-block h-full" style={{ verticalAlign: 'middle' }} />
            <img
              src={logoUrl}
              alt={clinicName}
              className="inline-block"
              style={{ verticalAlign: 'middle', maxHeight: 80 }}
            />
          </div>
        ) : blobUrl ? (
          <div
            style={{
              width: 300,
              height: 80,
            }}
            className="float-left"
          >
            <div className="inline-block h-full" style={{ verticalAlign: 'middle' }} />
            <img
              src={blobUrl}
              alt={clinicName}
              className="inline-block"
              style={{ verticalAlign: 'middle', maxHeight: 80 }}
            />
          </div>
        ) : null}
        <div
          style={{
            maxWidth: 300,
          }}
          className="flex flex-col float-right text-xs"
        >
          {(!!locationInfo?.name || !!clinicName) && (
            <div className="flex items-center gap-1">
              <div className="relative">
                <Icon icon={Icons.building} className="w-3 h-3 absolute left-0 top-0" />
              </div>
              <span className="ml-3">{locationInfo?.name || clinicName}</span>
            </div>
          )}
          {locationInfo?.address1 && (
            <div className="">
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <Icon icon={Icons.pinOnMap} className="w-3 h-3 absolute left-0 top-0" />
                  </div>
                  <span className="ml-3">{locationInfo?.address1 || ''}</span>
                </div>
                {!!locationInfo?.address2 && (
                  <span className="ml-4">{locationInfo?.address2 || ''}</span>
                )}
                <span className="ml-4">{`${locationInfo?.city || ''}, ${
                  locationInfo?.state || ''
                } ${locationInfo?.zip || ''}`}</span>
              </div>
            </div>
          )}
          {!!locationInfo?.phone && locationInfo?.phone !== 'null' && (
            <div className="flex items-center gap-1">
              <div className="relative">
                <PhoneIcon className="w-3 h-3 absolute left-0 top-0" />
              </div>
              <span className="ml-3">{formatPhone(locationInfo.phone)}</span>
            </div>
          )}
          {!!locationInfo?.fax && locationInfo?.fax !== 'null' && (
            <div className="flex items-center gap-1">
              <div className="relative">
                <Icon icon={Icons.fileText} className="w-3 h-3 absolute left-0 top-0" />
              </div>
              <span className="ml-3">{formatPhone(locationInfo.fax)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="w-full mb-12 text-sm text-gray-800 font-semibold">
      <div className="inline-block w-full">
        {logoUrl ? (
          <div
            style={{
              width: 300,
              height: 80,
            }}
            className="float-left"
          >
            <div className="inline-block h-full" style={{ verticalAlign: 'middle' }} />
            <img
              src={logoUrl}
              alt={clinicName}
              className="inline-block"
              style={{ verticalAlign: 'middle', maxHeight: 80 }}
            />
          </div>
        ) : (
          blobUrl && (
            <div
              style={{
                width: 300,
                height: 80,
              }}
              className="float-left"
            >
              <div
                className="inline-block h-full"
                style={{ verticalAlign: 'middle' }}
                // id="compressed-IMG"
              />
              <img
                src={blobUrl}
                alt={clinicName}
                className="inline-block"
                style={{ verticalAlign: 'middle', maxHeight: 80 }}
              />
            </div>
          )
        )}
        <div
          style={{
            maxWidth: 300,
          }}
          className="flex flex-col float-right text-xs"
        >
          {!!clinicName && (
            <div className="flex items-center gap-1">
              <Icon icon={Icons.building} className="w-3 h-3" />
              <span>{clinicName}</span>
            </div>
          )}
          {!!locationInfo?.address1 && (
            <div>
              <div className="flex flex-col">
                <div className="flex items-start gap-1">
                  <div className="relative">
                    <Icon icon={Icons.pinOnMap} className="w-3 h-3 absolute left-0 top-1" />
                  </div>
                  <span className="ml-3">{locationInfo?.address1 || ''}</span>
                </div>
                {!!locationInfo?.address2 && (
                  <span className="ml-4">{locationInfo?.address2 || ''}</span>
                )}
                <span className="ml-4">{`${locationInfo?.city || ''}, ${
                  locationInfo?.state || ''
                } ${locationInfo?.zip || ''}`}</span>
              </div>
            </div>
          )}
          {!!locationInfo?.phone && locationInfo?.phone !== 'null' && (
            <div className="flex items-center gap-1">
              <PhoneIcon className="w-3 h-3" />
              <span>{formatPhone(locationInfo.phone)}</span>
            </div>
          )}
          {!!locationInfo?.fax && locationInfo?.fax !== 'null' && (
            <div className="flex items-center gap-1">
              <Icon icon={Icons.fileText} className="w-3 h-3" />
              <span>{formatPhone(locationInfo.fax)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPrintLetterhead;
